// Process Flow Tracker Utility
// Handles tracking and management of inter-module process flows

import { supabase } from '../lib/supabase';

// Generate unique flow ID
const generateFlowId = () => {
  const year = new Date().getFullYear();
  const month = String(new Date().getMonth() + 1).padStart(2, '0');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `FLOW-${year}-${month}-${random}`;
};

// Start a new process flow
export const startProcessFlow = async (flowData) => {
  try {
    const flowId = generateFlowId();
    const currentTime = new Date().toISOString();

    // Create the main process flow record
    const { data: flowRecord, error: flowError } = await supabase
      .from('process_flows')
      .insert({
        flow_id: flowId,
        source_module: flowData.sourceModule,
        source_document_id: flowData.sourceDocumentId,
        source_document_type: flowData.sourceDocumentType,
        target_module: flowData.targetModule,
        target_document_id: flowData.targetDocumentId,
        target_document_type: flowData.targetDocumentType,
        process_type: flowData.processType,
        status: 'IN_PROGRESS',
        initiated_by: flowData.initiatedBy || 'system',
        initiated_at: currentTime,
        description: flowData.description,
        metadata: flowData.metadata || {}
      })
      .select()
      .single();

    if (flowError) throw flowError;

    // Create process steps based on template
    if (flowData.steps) {
      const stepRecords = flowData.steps.map((step, index) => ({
        flow_id: flowId,
        step_number: index + 1,
        step_name: step.name,
        step_type: step.type,
        status: 'PENDING',
        description: step.description,
        step_data: step.data || {}
      }));

      const { error: stepsError } = await supabase
        .from('process_flow_steps')
        .insert(stepRecords);

      if (stepsError) throw stepsError;
    }

    // Log the start action
    await logProcessFlowAction(flowId, 'CREATE', 'Process flow started', flowData.initiatedBy);

    return flowRecord;
  } catch (error) {
    console.error('Error starting process flow:', error);
    throw new Error(`Failed to start process flow: ${error.message}`);
  }
};

// Update process flow step status
export const updateProcessFlowStep = async (flowId, stepNumber, stepData) => {
  try {
    const currentTime = new Date().toISOString();
    const updateData = {
      status: stepData.status,
      step_data: stepData.data || {},
      updated_at: currentTime
    };

    if (stepData.status === 'IN_PROGRESS') {
      updateData.started_at = currentTime;
    } else if (stepData.status === 'COMPLETED' || stepData.status === 'FAILED') {
      updateData.completed_at = currentTime;
      if (stepData.startedAt) {
        updateData.duration_ms = new Date(currentTime).getTime() - new Date(stepData.startedAt).getTime();
      }
    }

    if (stepData.errorMessage) {
      updateData.error_message = stepData.errorMessage;
    }

    const { error } = await supabase
      .from('process_flow_steps')
      .update(updateData)
      .eq('flow_id', flowId)
      .eq('step_number', stepNumber);

    if (error) throw error;

    // Log the step update
    await logProcessFlowAction(flowId, 'UPDATE', `Step ${stepNumber} updated to ${stepData.status}`, stepData.updatedBy);

    return true;
  } catch (error) {
    console.error('Error updating process flow step:', error);
    throw new Error(`Failed to update process flow step: ${error.message}`);
  }
};

// Complete a process flow
export const completeProcessFlow = async (flowId, completionData) => {
  try {
    const currentTime = new Date().toISOString();

    const { error } = await supabase
      .from('process_flows')
      .update({
        status: 'COMPLETED',
        completed_at: currentTime,
        metadata: completionData.metadata || {},
        updated_at: currentTime
      })
      .eq('flow_id', flowId);

    if (error) throw error;

    // Log the completion
    await logProcessFlowAction(flowId, 'UPDATE', 'Process flow completed', completionData.completedBy);

    return true;
  } catch (error) {
    console.error('Error completing process flow:', error);
    throw new Error(`Failed to complete process flow: ${error.message}`);
  }
};

// Fail a process flow
export const failProcessFlow = async (flowId, failureData) => {
  try {
    const currentTime = new Date().toISOString();

    const { error } = await supabase
      .from('process_flows')
      .update({
        status: 'FAILED',
        completed_at: currentTime,
        metadata: { ...failureData.metadata, error: failureData.error },
        updated_at: currentTime
      })
      .eq('flow_id', flowId);

    if (error) throw error;

    // Log the failure
    await logProcessFlowAction(flowId, 'UPDATE', `Process flow failed: ${failureData.error}`, failureData.failedBy);

    return true;
  } catch (error) {
    console.error('Error failing process flow:', error);
    throw new Error(`Failed to mark process flow as failed: ${error.message}`);
  }
};

// Log process flow action
export const logProcessFlowAction = async (flowId, actionType, description, performedBy) => {
  try {
    const { error } = await supabase
      .from('process_flow_audit_logs')
      .insert({
        flow_id: flowId,
        action_type: actionType,
        action_description: description,
        performed_by: performedBy || 'system',
        performed_at: new Date().toISOString(),
        additional_data: {}
      });

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error logging process flow action:', error);
    // Don't throw error for logging failures to avoid breaking main flow
    return false;
  }
};

// Get process flow by ID
export const getProcessFlow = async (flowId) => {
  try {
    const { data: flowData, error: flowError } = await supabase
      .from('process_flows')
      .select('*')
      .eq('flow_id', flowId)
      .single();

    if (flowError) throw flowError;

    const { data: stepsData, error: stepsError } = await supabase
      .from('process_flow_steps')
      .select('*')
      .eq('flow_id', flowId)
      .order('step_number');

    if (stepsError) throw stepsError;

    return {
      ...flowData,
      steps: stepsData || []
    };
  } catch (error) {
    console.error('Error getting process flow:', error);
    throw new Error(`Failed to get process flow: ${error.message}`);
  }
};

// Get process flows by document
export const getProcessFlowsByDocument = async (module, documentId) => {
  try {
    const { data, error } = await supabase
      .from('process_flows')
      .select('*')
      .or(`source_document_id.eq.${documentId},target_document_id.eq.${documentId}`)
      .order('initiated_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error getting process flows by document:', error);
    throw new Error(`Failed to get process flows: ${error.message}`);
  }
};

// Get process flow template
export const getProcessFlowTemplate = async (templateId) => {
  try {
    const { data, error } = await supabase
      .from('process_flow_templates')
      .select('*')
      .eq('template_id', templateId)
      .eq('is_active', true)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error getting process flow template:', error);
    throw new Error(`Failed to get process flow template: ${error.message}`);
  }
};

// Track quotation to sales order conversion
export const trackQuotationToSalesOrder = async (quotation, salesOrder, initiatedBy) => {
  try {
    const flowData = {
      sourceModule: 'quotation',
      sourceDocumentId: quotation.quotation_number || quotation.id,
      sourceDocumentType: 'quotation',
      targetModule: 'sales_order',
      targetDocumentId: salesOrder.sales_order_id,
      targetDocumentType: 'sales_order',
      processType: 'CONVERSION',
      initiatedBy: initiatedBy || 'system',
      description: `Convert quotation ${quotation.quotation_number} to sales order ${salesOrder.sales_order_id}`,
      metadata: {
        quotationId: quotation.id,
        salesOrderId: salesOrder.sales_order_id,
        conversionType: 'quotation_to_sales_order'
      },
      steps: [
        {
          name: 'Validate Quotation',
          type: 'VALIDATION',
          description: 'Check if quotation is accepted and valid'
        },
        {
          name: 'Transform Data',
          type: 'TRANSFORMATION',
          description: 'Convert quotation data to sales order format'
        },
        {
          name: 'Create Sales Order',
          type: 'CREATION',
          description: 'Generate new sales order document'
        },
        {
          name: 'Update Quotation Status',
          type: 'UPDATE',
          description: 'Mark quotation as converted to SO'
        },
        {
          name: 'Verify Process',
          type: 'VERIFICATION',
          description: 'Confirm all data is correctly transferred'
        }
      ]
    };

    const flow = await startProcessFlow(flowData);

    // Update steps as the process progresses
    await updateProcessFlowStep(flow.flow_id, 1, { status: 'COMPLETED', updatedBy: initiatedBy });
    await updateProcessFlowStep(flow.flow_id, 2, { status: 'COMPLETED', updatedBy: initiatedBy });
    await updateProcessFlowStep(flow.flow_id, 3, { status: 'COMPLETED', updatedBy: initiatedBy });
    await updateProcessFlowStep(flow.flow_id, 4, { status: 'COMPLETED', updatedBy: initiatedBy });
    await updateProcessFlowStep(flow.flow_id, 5, { status: 'COMPLETED', updatedBy: initiatedBy });

    // Complete the flow
    await completeProcessFlow(flow.flow_id, { completedBy: initiatedBy });

    return flow;
  } catch (error) {
    console.error('Error tracking quotation to sales order conversion:', error);
    throw error;
  }
};

// Track sales order to purchase order conversion
export const trackSalesOrderToPurchaseOrder = async (salesOrder, purchaseOrder, initiatedBy) => {
  try {
    const flowData = {
      sourceModule: 'sales_order',
      sourceDocumentId: salesOrder.sales_order_id,
      sourceDocumentType: 'sales_order',
      targetModule: 'purchase_order',
      targetDocumentId: purchaseOrder.purchase_order_id,
      targetDocumentType: 'purchase_order',
      processType: 'CONVERSION',
      initiatedBy: initiatedBy || 'system',
      description: `Convert sales order ${salesOrder.sales_order_id} to purchase order ${purchaseOrder.purchase_order_id}`,
      metadata: {
        salesOrderId: salesOrder.sales_order_id,
        purchaseOrderId: purchaseOrder.purchase_order_id,
        conversionType: 'sales_order_to_purchase_order'
      },
      steps: [
        {
          name: 'Analyze Requirements',
          type: 'VALIDATION',
          description: 'Determine what needs to be purchased'
        },
        {
          name: 'Identify Suppliers',
          type: 'TRANSFORMATION',
          description: 'Map products to suppliers'
        },
        {
          name: 'Create Purchase Orders',
          type: 'CREATION',
          description: 'Generate purchase orders for suppliers'
        },
        {
          name: 'Link Documents',
          type: 'LINK',
          description: 'Link PO back to original SO'
        }
      ]
    };

    const flow = await startProcessFlow(flowData);

    // Update steps as the process progresses
    await updateProcessFlowStep(flow.flow_id, 1, { status: 'COMPLETED', updatedBy: initiatedBy });
    await updateProcessFlowStep(flow.flow_id, 2, { status: 'COMPLETED', updatedBy: initiatedBy });
    await updateProcessFlowStep(flow.flow_id, 3, { status: 'COMPLETED', updatedBy: initiatedBy });
    await updateProcessFlowStep(flow.flow_id, 4, { status: 'COMPLETED', updatedBy: initiatedBy });

    // Complete the flow
    await completeProcessFlow(flow.flow_id, { completedBy: initiatedBy });

    return flow;
  } catch (error) {
    console.error('Error tracking sales order to purchase order conversion:', error);
    throw error;
  }
};

// Get process flow statistics
export const getProcessFlowStatistics = async (dateRange = '7d') => {
  try {
    const startDate = new Date();
    if (dateRange === '7d') {
      startDate.setDate(startDate.getDate() - 7);
    } else if (dateRange === '30d') {
      startDate.setDate(startDate.getDate() - 30);
    } else if (dateRange === '90d') {
      startDate.setDate(startDate.getDate() - 90);
    }

    const { data, error } = await supabase
      .from('process_flows')
      .select('status, process_type, source_module, target_module')
      .gte('initiated_at', startDate.toISOString());

    if (error) throw error;

    const stats = {
      total: data.length,
      completed: data.filter(f => f.status === 'COMPLETED').length,
      inProgress: data.filter(f => f.status === 'IN_PROGRESS').length,
      failed: data.filter(f => f.status === 'FAILED').length,
      byProcessType: {},
      byModule: {}
    };

    // Calculate by process type
    data.forEach(flow => {
      stats.byProcessType[flow.process_type] = (stats.byProcessType[flow.process_type] || 0) + 1;
    });

    // Calculate by module
    data.forEach(flow => {
      stats.byModule[flow.source_module] = (stats.byModule[flow.source_module] || 0) + 1;
      stats.byModule[flow.target_module] = (stats.byModule[flow.target_module] || 0) + 1;
    });

    return stats;
  } catch (error) {
    console.error('Error getting process flow statistics:', error);
    throw new Error(`Failed to get process flow statistics: ${error.message}`);
  }
};
